import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, Sun, Moon, FileText } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { resolveImage } from "../lib/api";

const NAV = [
  { label: "Work", href: "#work" },
  { label: "Toolkit", href: "#toolkit" },
  { label: "About", href: "#about" },
  { label: "Expertise", href: "#expertise" },
  { label: "Contact", href: "#contact" },
];

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      title={`Switch to ${isDark ? "light" : "dark"} mode`}
      className="relative inline-flex items-center gap-1 w-14 h-8 rounded-full border border-[color:var(--card-border)] bg-[color:var(--card)] px-1 transition-colors"
    >
      <span
        className={`absolute top-1 w-6 h-6 rounded-full bg-[color:var(--ink)] text-[color:var(--bg)] grid place-items-center transition-transform duration-300 ${
          isDark ? "translate-x-6" : "translate-x-0"
        }`}
      >
        {isDark ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
      </span>
      <Sun className="w-3 h-3 ml-1 text-[color:var(--muted)]" />
      <Moon className="w-3 h-3 ml-auto mr-1 text-[color:var(--muted)]" />
    </button>
  );
};

const Header = ({ site }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const firstName = (site?.name || "Misbah Khan").split(" ")[0];
  const resumeUrl = site?.resume_url ? resolveImage(site.resume_url) : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur bg-[color:var(--bg)]/85 border-b border-[color:var(--card-border)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--ink)] text-[color:var(--bg)] font-display font-bold">
            {firstName[0]?.toLowerCase()}
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[color:var(--violet)]" />
          </span>
          <span className="font-display text-xl md:text-2xl font-semibold tracking-tight">
            {firstName}.
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-6">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="mk-link text-sm font-medium text-[color:var(--ink)]"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          {resumeUrl && (
            <a
              href={resumeUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="resume-button"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium bg-[color:var(--bg-2)] border border-[color:var(--card-border)] hover:bg-[color:var(--violet)] hover:text-white hover:border-[color:var(--violet)] transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Resume
            </a>
          )}
          <a href="#contact" className="inline-flex mk-btn mk-btn-violet !py-2 !px-4 text-sm">
            Let&apos;s talk <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[color:var(--card-border)]"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[color:var(--card-border)] bg-[color:var(--bg)]">
          <div className="px-5 py-5 flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium py-1"
              >
                {n.label}
              </a>
            ))}
            {resumeUrl && (
              <a
                href={resumeUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="resume-button-mobile"
                className="inline-flex items-center gap-2 text-base font-medium py-1"
              >
                <FileText className="w-4 h-4" /> Resume
              </a>
            )}
            <a href="#contact" onClick={() => setOpen(false)} className="mk-btn mk-btn-violet self-start">
              Let&apos;s talk <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
